// src/pages/PublicProfile/PublicProfile.js
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Seo from '../../components/Seo/Seo';
import AskQuestionModal from '../../components/Modals/AskQuestionModal';
import CurateQuestionModal from '../../components/Modals/CurateQuestionModal';
import UserAvatar from '../../components/UI/UserAvatar';
import { AuthContext } from '../../context/AuthContext';
import Layout1 from '../../components/QALayout/Layout1';
import Layout2 from '../../components/QALayout/Layout2';
import { getBackgroundPatternStyle } from '../../components/utils/backgroundPatterns';
import {
  buildPublicProfilePath,
  buildPublicQuestionPath,
} from '../../components/utils/profilePaths';

const PublicProfile = ({ defaultUserId, defaultUsername }) => {
  const {
    userId: paramUserId,
    username: legacyUsername,
  } = useParams();
  const { currentUser } = useContext(AuthContext);

  const navigate = useNavigate();
  const requestedUserId = paramUserId || defaultUserId || '';
  const requestedUsername =
    legacyUsername ||
    defaultUsername ||
    currentUser?.username ||
    '';
  const shouldCanonicalizeRoute = Boolean(paramUserId || legacyUsername);

  const [profile, setProfile] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [error, setError] = useState(null);
  const [showAskModal, setShowAskModal] = useState(false);
  const [showCurateModal, setShowCurateModal] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);

  const allTags = useMemo(
    () => Array.from(new Set(questions.flatMap((q) => q.tags || []))),
    [questions]
  );

  const isArchive = profile?.archiveProfile;
  const isOwnProfile = Boolean(
    currentUser &&
      profile &&
      String(currentUser._id || currentUser.id) === String(profile._id || profile.id)
  );
  const activeLayoutStyle = profile?.layoutStyle || '1';

  const fetchUserProfile = () => {
    const profileLookupUrl = requestedUserId
      ? `/api/users/public/id/${requestedUserId}`
      : `/api/users/public/${requestedUsername}`;

    fetch(profileLookupUrl, { cache: 'no-store' })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to fetch user profile (status: ${res.status})`);
        }
        return res.json();
      })
      .then((data) => {
        setProfile(data);
        setError(null);

        if (!shouldCanonicalizeRoute) {
          return;
        }

        const canonicalPath = buildPublicProfilePath(data);
        if (canonicalPath && window.location.pathname !== canonicalPath) {
          navigate(canonicalPath, { replace: true });
        }
      })
      .catch((err) => {
        console.error('Error fetching user profile:', err);
        setError(err.message);
      });
  };

  const fetchQuestions = (profileUsername) => {
    if (!profileUsername) {
      setLoadingQuestions(false);
      setQuestions([]);
      setSelectedQuestionId(null);
      return;
    }

    setLoadingQuestions(true);

    fetch(`/api/questions?username=${profileUsername}&role=receiver`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch questions');
        return res.json();
      })
      .then((data) => {
        const userQuestions = data
          .filter(
            (q) =>
              q.answer &&
              q.answer.trim() !== '' &&
              q.displayOnProfile &&
              !q.deletedByReceiver &&
              q.toUsername === profileUsername
          )
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        setQuestions(userQuestions);

        if (userQuestions.length > 0) {
          setSelectedQuestionId(userQuestions[0]._id || userQuestions[0].id);
        } else {
          setSelectedQuestionId(null);
        }

        setLoadingQuestions(false);
      })
      .catch((err) => {
        console.error('Error fetching questions:', err);
        setError(err.message);
        setLoadingQuestions(false);
      });
  };

  const displayedQuestions = selectedTag
    ? questions.filter((q) => (q.tags || []).includes(selectedTag))
    : questions;

  useEffect(() => {
    setProfile(null);
    setLoadingQuestions(false);
    setQuestions([]);
    setSelectedQuestionId(null);
    setError(null);
    fetchUserProfile();
  }, [navigate, requestedUserId, requestedUsername, shouldCanonicalizeRoute]);

  useEffect(() => {
    if (profile?.username) {
      fetchQuestions(profile.username);
    }
  }, [profile?.username]);

  useEffect(() => {
    if (displayedQuestions.length === 0) {
      setSelectedQuestionId(null);
      return;
    }

    const stillExists = displayedQuestions.some(
      (q) => (q._id || q.id) === selectedQuestionId
    );

    if (!stillExists) {
      setSelectedQuestionId(displayedQuestions[0]._id || displayedQuestions[0].id);
    }
  }, [displayedQuestions, selectedQuestionId]);

  const openAskModal = () => setShowAskModal(true);
  const closeAskModal = () => setShowAskModal(false);
  const handleQuestionPosted = () => fetchQuestions(profile?.username);

  const openCurateModal = () => setShowCurateModal(true);
  const closeCurateModal = () => setShowCurateModal(false);
  const handleCuratedQuestionCreated = () => {
    fetchQuestions(profile?.username);
    closeCurateModal();
  };

  const getQuestionPath = (q) => buildPublicQuestionPath(profile, q);

  const handleGridClick = (e) => {
    const card = e.target.closest('.qa-grid__item');
    if (!card) return;
    navigate(card.dataset.path);
  };

  const handleQuestionNavigate = (path) => {
    navigate(path);
  };

  const handleQuestionKeyDown = (ev, path) => {
    if (ev.key === 'Enter') {
      navigate(path);
    }
  };

  const handleQuestionSelect = (questionId) => {
    setSelectedQuestionId(questionId);
  };

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!profile) {
    return <div className="success-disappear-badge">Loading profile...</div>;
  }

  const title = `${profile.name} — Ask Me Anything | Ques`;
  const description = profile.bio
    ? `${profile.bio.substring(0, 150)}…`
    : `Ask ${profile.name} a question on Ques and get expert advice.`;
  const canonicalUrl = `https://askques.com${buildPublicProfilePath(profile)}`;
  const ogImageUrl = profile.avatarUrl || '/images/og-image.png';

  const resolvedAvatarUrl = profile.avatarUrl
    ? profile.avatarUrl === '/images/user-image.png'
      ? profile.avatarUrl
      : `${profile.avatarUrl}?v=${new Date().getTime()}`
    : '/images/user-image.png';

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.slice(0, 2).map((q) => {
      const questionUrl = `https://askques.com${buildPublicQuestionPath(profile, q)}`;

      return {
        '@type': 'Question',
        name: q.question,
        url: questionUrl,
        datePublished: new Date(q.createdAt).toISOString(),
        author: {
          '@type': 'Person',
          name: q.fromUsername || 'Anonymous',
        },
        acceptedAnswer: {
          '@type': 'Answer',
          text: q.answer,
          url: questionUrl,
          datePublished: new Date(q.updatedAt).toISOString(),
          author: {
            '@type': 'Person',
            name: profile.name,
          },
          upvoteCount: 0,
        },
      };
    }),
  };

  const themeStyles = {
    '--theme-primary': profile.themePrimary || '#1A140F',
    '--theme-accent': profile.themeAccent || '#FAF6F2',
    '--theme-primary-text': profile.themePrimaryText || '#1A140F',
    '--theme-accent-text': profile.themeAccentText || '#FAF6F2',
    '--theme-background': profile.themeBackground || '#FFFFFF',
    '--theme-background-text': profile.themeBackgroundText || '#1A140F',
  };

  const profilePageStyle = getBackgroundPatternStyle({
    pattern: profile?.backgroundPattern,
    opacity: profile?.backgroundPatternOpacity,
    backgroundColor: profile?.themeBackground || '#FFFFFF',
    foregroundColor: profile?.themeBackgroundText || '#1A140F',
  });

  return (
    <>
      <Seo
        title={title}
        description={description}
        canonicalUrl={canonicalUrl}
        ogImageUrl={ogImageUrl}
      />

      <main
        role="main"
        className={`public-profile-wrapper profile-page-shell${
          isOwnProfile ? ' profile-page-shell--with-banner' : ''
        }`}
        style={themeStyles}
      >
        {(isOwnProfile || isArchive) && (
          <div className="profile-banner">
            <p>
              {isOwnProfile
                ? 'You are viewing your own public page'
                : `This is a curated Q&A archive based on publicly available interviews, podcasts, and social posts. Not affiliated with ${profile.name}.`}
            </p>
          </div>
        )}

        <section className="profile-page" style={profilePageStyle}>
          <div className="profile-page__content top-xs">
            <section className="profile-sidebar" aria-label="User Profile">
              <article className="card">
                <figure className="user-image">
                  <UserAvatar
                    avatarUrl={resolvedAvatarUrl}
                    userName={profile.name}
                    size={80}
                    alt={`Avatar of ${profile.name} for expert Q&A platform`}
                  />
                </figure>

                {isArchive && !isOwnProfile && (
                  <span className="badge-non-affiliated">Non-affiliated profile</span>
                )}

                <div>
                  <h1 className="h5">{profile.name}</h1>
                </div>

                {profile.bio && <p>{profile.bio}</p>}
              </article>

              {isOwnProfile ? (
                <button
                  className="margin-top-m"
                  id="curate-qa-cta"
                  onClick={openCurateModal}
                  aria-label="Curate your own Q&As"
                >
                  Curate my own Q&As
                </button>
              ) : (
                !isArchive && (
                  <button
                    className="margin-top-m"
                    id="ask-question-cta"
                    onClick={openAskModal}
                    aria-label={`Ask ${profile.name} a Question`}
                  >
                    Ask {profile.name} a Question
                  </button>
                )
              )}
            </section>

            {allTags.length > 0 && (
              <div className="profile-tag-filter">
                <h6 className="section-title text-center">Tags</h6>
                <button
                  className={!selectedTag ? 'active' : ''}
                  onClick={() => setSelectedTag(null)}
                >
                  All
                </button>
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    className={selectedTag === tag ? 'active' : ''}
                    onClick={() => setSelectedTag(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}

            {loadingQuestions ? (
              <div className="success-disappear-badge">Loading questions...</div>
            ) : displayedQuestions.length > 0 ? (
              activeLayoutStyle === '2' ? (
                <Layout2
                  questions={displayedQuestions}
                  selectedQuestionId={selectedQuestionId}
                  getQuestionPath={getQuestionPath}
                  onSelectQuestion={handleQuestionSelect}
                  onQuestionClick={handleQuestionNavigate}
                  onQuestionKeyDown={handleQuestionKeyDown}
                />
              ) : (
                <Layout1
                  questions={displayedQuestions}
                  onItemClick={handleGridClick}
                  onItemKeyDown={handleQuestionKeyDown}
                  getQuestionPath={getQuestionPath}
                  showSource={true}
                />
              )
            ) : null}
          </div>
        </section>

        {showAskModal && !isOwnProfile && (
          <AskQuestionModal
            onClose={closeAskModal}
            onQuestionPosted={handleQuestionPosted}
            recipient={profile}
            useProfileTheme
          />
        )}

        {showCurateModal && isOwnProfile && (
          <CurateQuestionModal
            onClose={closeCurateModal}
            onQuestionCreated={handleCuratedQuestionCreated}
            currentUser={currentUser}
            isArchive={isArchive}
            useProfileTheme
          />
        )}

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
        />
      </main>
    </>
  );
};

export default PublicProfile;
